import { get, noop, omit, pick } from 'lodash';

import type { Diagnostics, Request, Response } from '@paychex/core/types/data';
import { DiagnosticOptions, Endpoint } from '..';

export type { Diagnostics }

export type ResponseError = Error & { response: Response };

/**
 * Creates a diagnostics function for use with `withDiagnostics` in `@paychex/core`.
 *
 * @param options Provides the objects necessary to create the diagnostics function.
 * @param endpoints The endpoints to test as part of a diagnostics routine.
 * @returns A diagnostics function for use with `withDiagnostics`.
 * @example
 * ```js
 * import { tracker } from '~/path/to/tracker.js';
 * import { fetch, createRequest } from '~/path/to/datalayer.js';
 *
 * const endpoints = config.endpoints.concat([
 *     {
 *         "name": "My Project Health Endpoint",
 *         "base": "my-project",
 *         "path": "/health",
 *     }
 * ];
 *
 * const options = {
 *   fetch,
 *   createRequest,
 *   tracker,
 * };
 *
 * const diagnostics = internal.data.utils.createDiagnostics(options, endpoints);
 * const pipeline = core.data.utils.withDiagnostics(fetch, diagnostics);
 * ```
 */
export function createDiagnostics({ fetch, createRequest, tracker }: DiagnosticOptions, endpoints: Endpoint[]): Diagnostics {

    const diagnosed = new Map();

    function asRequest(req: Endpoint): Request {
        return createRequest(req, null, undefined);
    }

    async function getNetworkInfo() {
        const ip = await fetch(createRequest({
            path: '/',
            protocol: 'https',
            base: 'api.ipify.org',
            headers: { accept: 'application/json' }
        }, { format: 'json' }));
        const isp = await fetch(createRequest({
            protocol: 'https',
            path: '/rest/ip/:ip',
            base: 'whois.arin.net',
            headers: { accept: 'application/json' }
        }, { ip: get(ip, 'data.ip', '0.0.0.0') }));
        return {
            isp: get(isp, 'data.net.name.$', 'Unknown'),
            org: get(isp, 'data.net.orgRef.@handle', 'Unknown')
        };
    }

    function asResultPromise(request: Request) {
        const fromResponse = (response: Response) => {
            const success = get(request, 'success', 200);
            const name = get(request, 'name', request.path);
            const result = response.status === success ?
                `${response.status}: OK` :
                `${response.status}: ${get(response, 'statusText', 'Unknown')}`;
            return { name, result };
        };
        const fromError = (error: ResponseError) => fromResponse(error.response);
        return fetch(request).then(fromResponse, fromError).catch(noop);
    }

    return async function diagnostics(req: Request) {
        if (get(req, 'ignore.diagnostics'))
            return;
        if (!diagnosed.has(req.base)) {
            diagnosed.set(req.base, true);
            const requests = endpoints.map(asRequest);
            const responses = requests.map(asResultPromise);
            const request = pick(req, ['url', 'method', 'headers']);
            // remove potentially sensitive information before tracking
            request.headers = omit(request.headers, ['authorization']);
            Promise.all([
                getNetworkInfo(),
                Promise.all(responses),
            ]).then(function track([network, results]) {
                tracker.event('NETWORK_DIAGNOSTICS', {
                    request,
                    results,
                    network,
                });
            });
        }
    }

}
